/**
 * 登录提示浮窗（复用组件）
 * ---------------------------------------------------------------------------
 * 适用场景：
 *   1. ProductDetail 等公共页点「询盘/立即购买」→ 未登录 → 原地弹窗（不跳转）
 *   2. RequireAuth 路由守卫拦截 → 弹窗
 * 交互约定：
 *   · 半透明 backdrop（0.35），当前页面保留为可见背景；
 *   · 「随便看看」= 关闭浮窗（回原页面继续浏览）；
 *   · 「去登录」= 跳 /buyer/login 并带 state.from，登录后回跳原页。
 */

import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useNavigate } from 'react-router-dom';
import { usePrefs } from '@/store/BuyerPrefsContext';

interface LoginPromptDialogProps {
  /** 是否显示 */
  open: boolean;
  /** 点击「随便看看」或关闭（backdrop/ESC）时回调：由调用方决定去向 */
  onClose: () => void;
  /** 点击「去登录」时回跳的路径（默认当前路径，登录成功后回跳） */
  from?: string;
  /** State to restore after successful sign-in (for example the home inquiry text). */
  returnState?: unknown;
  /** 标题文案（默认「请先登录后查看」） */
  title?: string;
  /** 副文案（默认通用描述） */
  description?: string;
}

export default function LoginPromptDialog({
  open,
  onClose,
  from,
  returnState,
  title,
  description,
}: LoginPromptDialogProps) {
  const navigate = useNavigate();
  const { t } = usePrefs();
  const dialogTitle = title ?? t('auth.promptTitle');
  const dialogDesc = description ?? t('auth.promptDesc');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      // 半透明 backdrop：让当前页面作为背景保留，若隐若现，避免"整页变灰"的生硬感
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.35)' } },
      }}
      className="buyer-login-prompt"
      sx={{ '& .MuiDialog-paper': {
        borderRadius: '22px',
        background: 'radial-gradient(ellipse at 100% 0, rgba(221,248,236,.50), transparent 43%), radial-gradient(ellipse at 0 35%, rgba(225,248,238,.35), transparent 24%), #fff',
        m: 2, width: '84%', maxWidth: 460,
        boxShadow: '0 18px 48px rgba(13,39,30,.20)',
      } }}
    >
      <DialogContent sx={{ textAlign: 'center', px: { xs: 2, sm: 3 }, pt: 3.25, pb: 0 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            bgcolor: 'rgba(221,248,237,.65)',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2.25,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 27 }} />
        </Box>
        <Typography component="h2" sx={{ fontSize: { xs: 16, sm: 19 }, lineHeight: 1.4, fontWeight: 700, mb: 1.5, color: '#111' }}>
          {dialogTitle}
        </Typography>
        <Typography sx={{ fontSize: { xs: 13, sm: 14 }, lineHeight: 1.65, color: '#818784' }}>
          {dialogDesc}
        </Typography>
      </DialogContent>
      <DialogActions sx={{
        display: 'grid', gridTemplateColumns: '1.2fr 1fr', alignItems: 'stretch',
        px: { xs: 2, sm: 3 }, pt: 2, pb: 3, gap: 1.25,
        '& > .MuiButton-root': {
          m: 0, minWidth: 0, height: 44, px: { xs: .75, sm: 1.5 }, py: 0,
          whiteSpace: 'nowrap', textTransform: 'none', borderRadius: '9px',
          fontSize: { xs: 12, sm: 14 }, fontWeight: 500,
          '& .MuiButton-startIcon': { ml: 0, mr: .75, '& svg': { fontSize: 19 } },
        },
      }}>
        <Button variant="outlined" startIcon={<HomeOutlinedIcon />} onClick={onClose} sx={{ color: '#00AD82', borderColor: '#45CCA6', '&:hover': { borderColor: '#00AD82', bgcolor: '#F0FCF6' } }}>
          {t('auth.browseAround')}
        </Button>
        <Button
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={() => navigate('/buyer/login', { state: { from: from || window.location.pathname, returnState } })}
          sx={{ background: 'linear-gradient(115deg, #32C4A0, #00AC80 65%, #12B78B)', boxShadow: '0 4px 10px rgba(0,174,128,.20)', '&:hover': { boxShadow: '0 5px 12px rgba(0,174,128,.25)' } }}
        >
          {t('auth.goLogin')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
